import React from "react";
import {
  Button,
  Col,
  Divider,
  Drawer,
  List,
  Row,
  Space,
  Tag,
  Typography,
  notification,
  Spin,
} from "antd";
import { FilePdfOutlined, FileExcelOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Title, Text } = Typography;

export default function VotingSummaryDrawer({
  visible,
  onClose,
  selectedRecord,
  loggedInUserName,
  loading,
}) {
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, description) => {
    api[type]({
      message: message,
      description: description,
      placement: "bottomLeft",
    });
  };

  // Format timestamp to show only date and time (hour:minute)
  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === "-") return "-";
    return dayjs(timestamp).format("YYYY-MM-DD HH:mm");
  };

  // Capitalize first letter of vote
  const capitalizeVote = (vote) => {
    if (!vote) return "No Response";
    return vote.charAt(0).toUpperCase() + vote.slice(1).toLowerCase();
  };

  // PDF Export Handler
  const handleExportPDF = () => {
    if (!selectedRecord) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Professional Color Palette - System Theme
    const colors = {
      primary: [114, 46, 209], // Primary Purple #722ed1
      primaryDark: [90, 36, 165], // Darker Purple
      success: [82, 196, 26], // Green
      error: [255, 77, 79], // Red
      warning: [250, 173, 20], // Orange
      neutral: [140, 140, 140], // Gray
      lightGray: [245, 245, 245], // Light Gray
      headerBg: [250, 250, 250], // Section Header Background
      white: [255, 255, 255],
      dark: [38, 38, 38], // Dark Text
    };

    // ========== HEADER SECTION ==========
    // Modern gradient-style header
    doc.setFillColor(...colors.primary);
    doc.rect(0, 0, pageWidth, 45, "F");

    // Add accent line
    doc.setFillColor(...colors.primaryDark);
    doc.rect(0, 45, pageWidth, 2, "F");

    // Title
    doc.setTextColor(...colors.white);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Voting Summary Report", pageWidth / 2, 20, { align: "center" });

    // Subtitle with document info
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Meeting Name: ${selectedRecord.meetingName || "Meeting Report"}`,
      pageWidth / 2,
      30,
      { align: "center" },
    );

    // Generation timestamp and user info
    doc.setFontSize(9);
    doc.setTextColor(230, 230, 230);
    doc.text(
      `Generated by: ${loggedInUserName || 'System User'} | ${dayjs().format("MMMM DD, YYYY • HH:mm:ss")}`,
      pageWidth / 2,
      38,
      { align: "center" },
    );

    yPos = 57;

    // Helper function to draw section header
    const drawSectionHeader = (title, y) => {
      doc.setFillColor(...colors.headerBg);
      doc.roundedRect(14, y, pageWidth - 28, 10, 1, 1, "F");
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.line(14, y + 10, pageWidth - 14, y + 10);

      doc.setTextColor(...colors.primary);
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text(title, 18, y + 7);

      return y + 15;
    };

    // ========== MEETING INFORMATION SECTION ==========
    yPos = drawSectionHeader("Meeting Information", yPos);

    // Helper function to format dates
    const formatDate = (dateString) => {
      if (!dateString || dateString === "-") return "-";
      return dayjs(dateString).format("MMMM DD, YYYY • hh:mm A");
    };

    const meetingDetails = [
      ["Meeting Name", selectedRecord.meetingName || "-"],
      ["Start Date & Time", formatDate(selectedRecord.meetingStart)],
      ["End Date & Time", formatDate(selectedRecord.meetingEnd)],
      ["Committee Type", selectedRecord.committeeType || "-"],
      ["Committee Name", selectedRecord.committeeName || "-"],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: meetingDetails,
      theme: "plain",
      styles: {
        fontSize: 10,
        cellPadding: 5,
        lineColor: [230, 230, 230],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: {
          fontStyle: "bold",
          fillColor: colors.lightGray,
          textColor: colors.dark,
          cellWidth: 65,
        },
        1: {
          cellWidth: "auto",
          textColor: [60, 60, 60],
        },
      },
      margin: { left: 14, right: 14 },
      didDrawCell: function (data) {
        if (data.section === "body") {
          doc.setDrawColor(230, 230, 230);
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // ========== DOCUMENT INFORMATION SECTION ==========
    yPos = drawSectionHeader("Document Information", yPos);

    const documentDetails = [
      ["Paper Category", selectedRecord.paperCategory || "-"],
      ["Paper Name", selectedRecord.paperName || "-"],
      ["Paper Code", selectedRecord.paperCode || "-"],
      ["Document Name", selectedRecord.documentName || "-"],
      ["Comment", selectedRecord.comment || "-"],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: documentDetails,
      theme: "plain",
      styles: {
        fontSize: 10,
        cellPadding: 5,
        lineColor: [230, 230, 230],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: {
          fontStyle: "bold",
          fillColor: colors.lightGray,
          textColor: colors.dark,
          cellWidth: 65,
        },
        1: {
          cellWidth: "auto",
          textColor: [60, 60, 60],
        },
      },
      margin: { left: 14, right: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // ========== APPROVER INFORMATION SECTION ==========
    yPos = drawSectionHeader("Approver Information", yPos);

    const approverDetails = [
      // ["Approver Name", selectedRecord.approver?.name || "-"],
      // ["Approval Action Time", formatDate(selectedRecord.approver?.actionTime)],
      ["Voting End Time", formatDate(selectedRecord.votingEnd)],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: approverDetails,
      theme: "plain",
      styles: {
        fontSize: 10,
        cellPadding: 5,
        lineColor: [230, 230, 230],
        lineWidth: 0.1,
      },
      columnStyles: {
        0: {
          fontStyle: "bold",
          fillColor: colors.lightGray,
          textColor: colors.dark,
          cellWidth: 65,
        },
        1: {
          cellWidth: "auto",
          textColor: [60, 60, 60],
        },
      },
      margin: { left: 14, right: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 12;

    // Check if we need a new page
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 20;
    }

    // ========== VOTING RESULT SECTION ==========
    yPos = drawSectionHeader("Voting Result & Statistics", yPos);

    // Result status box with shadow effect
    const votingResult = selectedRecord.votingResult || "Pending";
    const resultColor =
      votingResult === "Approved"
        ? colors.success
        : votingResult === "Rejected"
          ? colors.error
          : colors.neutral;

    // Result badge
    doc.setFillColor(...resultColor);
    doc.roundedRect(14, yPos, 70, 12, 3, 3, "F");
    doc.setTextColor(...colors.white);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(votingResult.toUpperCase(), 49, yPos + 8, { align: "center" });

    doc.setTextColor(...colors.dark);
    doc.setFont("helvetica", "normal");
    yPos += 18;

    // Voting statistics cards
    const votingCounts = selectedRecord.votingCounts || {
      approved: 0,
      rejected: 0,
      abstain: 0,
    };

    // Card dimensions
    const cardWidth = (pageWidth - 40) / 3;
    const cardHeight = 28;
    const cardSpacing = 4;
    const startX = 14;

    // Approved Card
    doc.setFillColor(240, 255, 244);
    doc.setDrawColor(...colors.success);
    doc.setLineWidth(0.5);
    doc.roundedRect(startX, yPos, cardWidth, cardHeight, 2, 2, "FD");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text("APPROVED", startX + cardWidth / 2, yPos + 8, { align: "center" });
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.success);
    doc.text(
      String(votingCounts.approved || 0),
      startX + cardWidth / 2,
      yPos + 21,
      { align: "center" },
    );

    // Rejected Card
    doc.setFillColor(255, 245, 245);
    doc.setDrawColor(...colors.error);
    doc.roundedRect(
      startX + cardWidth + cardSpacing,
      yPos,
      cardWidth,
      cardHeight,
      2,
      2,
      "FD",
    );
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(
      "REJECTED",
      startX + cardWidth + cardSpacing + cardWidth / 2,
      yPos + 8,
      { align: "center" },
    );
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.error);
    doc.text(
      String(votingCounts.rejected || 0),
      startX + cardWidth + cardSpacing + cardWidth / 2,
      yPos + 21,
      { align: "center" },
    );

    // Abstain Card
    doc.setFillColor(250, 250, 250);
    doc.setDrawColor(...colors.neutral);
    doc.roundedRect(
      startX + (cardWidth + cardSpacing) * 2,
      yPos,
      cardWidth,
      cardHeight,
      2,
      2,
      "FD",
    );
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(
      "ABSTAIN",
      startX + (cardWidth + cardSpacing) * 2 + cardWidth / 2,
      yPos + 8,
      { align: "center" },
    );
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...colors.neutral);
    doc.text(
      String(votingCounts.abstain || 0),
      startX + (cardWidth + cardSpacing) * 2 + cardWidth / 2,
      yPos + 21,
      { align: "center" },
    );

    yPos += cardHeight + 15;

    // Check if we need a new page
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 20;
    }

    // ========== VOTING SUMMARY TABLE ==========
    yPos = drawSectionHeader("Detailed Voting Records", yPos);

    // Prepare voting data
    const votes = selectedRecord.votes || [];
    const sortedVotes = [...votes].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp) : null;
      const tb = b.timestamp ? new Date(b.timestamp) : null;
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return ta - tb;
    });

    const votingData = sortedVotes.map((vote, index) => [
      String(index + 1),
      vote.participant || "-",
      capitalizeVote(vote.vote),
      formatDate(vote.timestamp),
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [["#", "Participant Name", "Vote Decision", "Timestamp"]],
      body:
        votingData.length > 0
          ? votingData
          : [["", "No voting records available", "", ""]],
      theme: "striped",
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontStyle: "bold",
        halign: "center",
        fontSize: 10,
        cellPadding: 6,
      },
      styles: {
        fontSize: 9,
        cellPadding: 5,
        lineColor: [230, 230, 230],
        lineWidth: 0.1,
      },
      alternateRowStyles: {
        fillColor: [252, 252, 252],
      },
      columnStyles: {
        0: {
          cellWidth: 12,
          halign: "center",
          fontStyle: "bold",
          textColor: [100, 100, 100],
        },
        1: { cellWidth: 70 },
        2: { cellWidth: 35, halign: "center", fontStyle: "bold" },
        3: { cellWidth: "auto", fontSize: 8, textColor: [100, 100, 100] },
      },
      margin: { left: 14, right: 14 },
      didParseCell: function (data) {
        if (data.column.index === 2 && data.section === "body") {
          const vote = data.cell.raw;
          if (vote === "Approved") {
            data.cell.styles.textColor = colors.success;
            data.cell.styles.fontStyle = "bold";
          } else if (vote === "Rejected") {
            data.cell.styles.textColor = colors.error;
            data.cell.styles.fontStyle = "bold";
          } else if (vote === "Abstain") {
            data.cell.styles.textColor = colors.neutral;
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = [180, 180, 180];
            data.cell.styles.fontStyle = "italic";
          }
        }
      },
    });

    // ========== FOOTER ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Footer line
      doc.setDrawColor(...colors.primary);
      doc.setLineWidth(0.5);
      doc.line(14, pageHeight - 20, pageWidth - 14, pageHeight - 20);

      // Page number
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.setFont("helvetica", "normal");
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 12, {
        align: "center",
      });

      // Footer text
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        "Octoplex Voting Summary Report - Confidential",
        14,
        pageHeight - 12,
      );

      doc.text(dayjs().format("YYYY-MM-DD"), pageWidth - 14, pageHeight - 12, {
        align: "right",
      });
    }

    // Save PDF
    const fileName = `Voting_Summary_${selectedRecord.documentName?.replace(/\s+/g, "_")}_${dayjs().format("YYYYMMDD_HHmm")}.pdf`;
    doc.save(fileName);

    openNotificationWithIcon(
      "success",
      "PDF Export Successful",
      `Report has been saved as ${fileName}`,
    );
  };

  // Excel Export Handler
  const handleExportExcel = () => {
    if (!selectedRecord) return;

    // Helper function to format dates for Excel
    const formatDate = (dateString) => {
      if (!dateString || dateString === "-") return "-";
      return dayjs(dateString).format("MMMM DD, YYYY • hh:mm A");
    };

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // ========== SHEET 1: MEETING & DOCUMENT INFO ==========
    const infoData = [
      ["VOTING SUMMARY REPORT"],
      [""],
      ["Meeting Information"],
      ["Meeting Name", selectedRecord.meetingName || "-"],
      ["Start Date & Time", formatDate(selectedRecord.meetingStart)],
      ["End Date & Time", formatDate(selectedRecord.meetingEnd)],
      ["Committee Type", selectedRecord.committeeType || "-"],
      ["Committee Name", selectedRecord.committeeName || "-"],
      [""],
      ["Document Information"],
      ["Paper Category", selectedRecord.paperCategory || "-"],
      ["Paper Name", selectedRecord.paperName || "-"],
      ["Paper Code", selectedRecord.paperCode || "-"],
      ["Document Name", selectedRecord.documentName || "-"],
      ["Comment", selectedRecord.comment || "-"],
      [""],
      ["Approver Information"],
      // ["Approver Name", selectedRecord.approver?.name || "-"],
      // ["Approval Action Time", formatDate(selectedRecord.approver?.actionTime)],
      ["Voting End Time", formatDate(selectedRecord.votingEnd)],
      [""],
      ["Generated By", loggedInUserName || "System User"],
      ["Generated On", dayjs().format("MMMM DD, YYYY • HH:mm:ss")],
    ];

    const infoSheet = XLSX.utils.aoa_to_sheet(infoData);

    // Set column widths
    infoSheet["!cols"] = [{ wch: 25 }, { wch: 50 }];

    XLSX.utils.book_append_sheet(workbook, infoSheet, "Meeting Info");

    // ========== SHEET 2: VOTING RESULTS ==========
    const votingCounts = selectedRecord.votingCounts || {
      approved: 0,
      rejected: 0,
      abstain: 0,
    };
    const votingResult = selectedRecord.votingResult || "Pending";

    const resultsData = [
      ["VOTING RESULTS & STATISTICS"],
      [""],
      ["Overall Result", votingResult],
      [""],
      ["Vote Type", "Count"],
      ["Approved", votingCounts.approved || 0],
      ["Rejected", votingCounts.rejected || 0],
      ["Abstain", votingCounts.abstain || 0],
      [""],
      [
        "Total Votes",
        (votingCounts.approved || 0) +
          (votingCounts.rejected || 0) +
          (votingCounts.abstain || 0),
      ],
    ];

    const resultsSheet = XLSX.utils.aoa_to_sheet(resultsData);

    // Set column widths
    resultsSheet["!cols"] = [{ wch: 20 }, { wch: 15 }];

    XLSX.utils.book_append_sheet(workbook, resultsSheet, "Voting Results");

    // ========== SHEET 3: DETAILED VOTING RECORDS ==========
    const votes = selectedRecord.votes || [];
    const sortedVotes = [...votes].sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp) : null;
      const tb = b.timestamp ? new Date(b.timestamp) : null;
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return ta - tb;
    });

    const votingRecordsData = [
      ["DETAILED VOTING RECORDS"],
      [""],
      ["#", "Participant Name", "Vote Decision", "Timestamp"],
    ];

    sortedVotes.forEach((vote, index) => {
      votingRecordsData.push([
        index + 1,
        vote.participant || "-",
        capitalizeVote(vote.vote),
        formatDate(vote.timestamp),
      ]);
    });

    if (sortedVotes.length === 0) {
      votingRecordsData.push(["", "No voting records available", "", ""]);
    }

    const votingRecordsSheet = XLSX.utils.aoa_to_sheet(votingRecordsData);

    // Set column widths
    votingRecordsSheet["!cols"] = [
      { wch: 5 },
      { wch: 35 },
      { wch: 15 },
      { wch: 30 },
    ];

    XLSX.utils.book_append_sheet(
      workbook,
      votingRecordsSheet,
      "Voting Records",
    );

    // Save the file
    const fileName = `Voting_Summary_${selectedRecord.documentName?.replace(/\s+/g, "_")}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    openNotificationWithIcon(
      "success",
      "Excel Export Successful",
      `Report has been saved as ${fileName}`,
    );
  };

  return (
    <>
      {contextHolder}
      <Drawer
        title="Detailed Voting Summary"
        open={visible}
        onClose={onClose}
        width={900}
        footer={
          <Space>
            <Button
              key="pdf"
              icon={<FilePdfOutlined />}
              onClick={handleExportPDF}
              type="primary"
            >
              Export to PDF
            </Button>
            <Button
              key="excel"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              style={{
                backgroundColor: "#52c41a",
                color: "white",
                borderColor: "#52c41a",
              }}
            >
              Export to Excel
            </Button>
            <Button key="close" onClick={onClose}>
              Close
            </Button>
          </Space>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Spin size="large" tip="Loading voting summary..." />
          </div>
        ) : selectedRecord ? (
          <div>
            <div style={{ padding: 8 }}>
              <Row gutter={24}>
                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Meeting Name</Text>
                    <div>
                      <Text>{selectedRecord.meetingName || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>End Date & Time</Text>
                    <div>
                      <Text>{formatTimestamp(selectedRecord.meetingEnd)}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Committee Name</Text>
                    <div>
                      <Text>{selectedRecord.committeeName || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Paper Name</Text>
                    <div>
                      <Text>{selectedRecord.paperName || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Document Name</Text>
                    <div>
                      <Text>{selectedRecord.documentName || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Comment</Text>
                    <div>
                      <Text>{selectedRecord.comment || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Voting End</Text>
                    <div>
                      <Text>{formatTimestamp(selectedRecord.votingEnd)}</Text>
                    </div>
                  </div>
                </Col>

                <Col span={12}>
                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Start Date & Time</Text>
                    <div>
                      <Text>{formatTimestamp(selectedRecord.meetingStart)}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Committee Type</Text>
                    <div>
                      <Text>{selectedRecord.committeeType || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Paper Category</Text>
                    <div>
                      <Text>{selectedRecord.paperCategory || "-"}</Text>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <Text strong>Paper Code</Text>
                    <div>
                      <Text>{selectedRecord.paperCode || "-"}</Text>
                    </div>
                  </div>

                  {/* <div style={{ marginBottom: 16 }}>
                    <Text strong>Approver</Text>
                    <div>
                      <Text>{selectedRecord.approver?.name || "-"}</Text>
                    </div>
                  </div> */}

                  {/* <div style={{ marginBottom: 16 }}>
                    <Text strong>Approval Action</Text>
                    <div>
                      <Text>{selectedRecord.approver?.actionTime || "-"}</Text>
                    </div>
                  </div> */}
                </Col>
              </Row>
            </div>

            <Divider />

            <Title level={5}>Voting Result</Title>
            <Row
              gutter={[24, 12]}
              style={{ marginBottom: 12 }}
            >
              <Col xs={24} md={8}>
                <Space direction="vertical" size={4}>
                  <Text strong>Voting Result</Text>
                  <Tag
                    color={
                      selectedRecord.votingResult === "Approved"
                        ? "success"
                        : selectedRecord.votingResult === "Rejected"
                          ? "error"
                          : "default"
                    }
                  >
                    {selectedRecord.votingResult || "-"}
                  </Tag>
                </Space>
              </Col>
              <Col xs={24} md={5}>
                <Space direction="vertical" size={4} style={{ width: '100%', alignItems: 'center' }}>
                  <Text strong>Approved Count</Text>
                  <Tag color="success" style={{ minWidth: 36, textAlign: 'center', display: 'block' }}>
                    {selectedRecord.votingCounts?.approved ?? 0}
                  </Tag>
                </Space>
              </Col>
              <Col xs={24} md={5}>
                <Space direction="vertical" size={4} style={{ width: '100%', alignItems: 'center' }}>
                  <Text strong>Rejected Count</Text>
                  <Tag color="error" style={{ minWidth: 36, textAlign: 'center', display: 'block' }}>
                    {selectedRecord.votingCounts?.rejected ?? 0}
                  </Tag>
                </Space>
              </Col>
              <Col xs={24} md={6}>
                <Space direction="vertical" size={4} style={{ width: '100%', alignItems: 'center' }}>
                  <Text strong>Abstain Count</Text>
                  <Tag style={{ minWidth: 36, textAlign: 'center', display: 'block' }}>
                    {selectedRecord.votingCounts?.abstain ?? 0}
                  </Tag>
                </Space>
              </Col>
            </Row>

            <Title level={5}>Voting Summary</Title>
            <List
              dataSource={selectedRecord.votes.sort((a, b) => {
                const ta = a.timestamp ? new Date(a.timestamp) : null;
                const tb = b.timestamp ? new Date(b.timestamp) : null;
                if (!ta && !tb) return 0;
                if (!ta) return 1;
                if (!tb) return -1;
                return ta - tb;
              })}
              style={{ marginTop: 8 }}
              renderItem={(item) => {
                // Normalize vote text and determine color
                const normalizeVote = (vote) => {
                  if (!vote) return { text: "No Response", color: "default", textColor: "#8c8c8c" };
                  const v = vote.toLowerCase();
                  if (v === "approve" || v === "approved") {
                    return { text: "Approved", color: "success", textColor: "#52c41a" };
                  } else if (v === "reject" || v === "rejected") {
                    return { text: "Rejected", color: "error", textColor: "#ff4d4f" };
                  } else if (v === "abstain") {
                    return { text: "Abstain", color: "default", textColor: "#8c8c8c" };
                  }
                  return { text: vote, color: "default", textColor: "#8c8c8c" };
                };
                
                const voteInfo = normalizeVote(item.vote);
                
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <div>{item.participant}</div>
                          <div>
                            <Tag color={voteInfo.color}>
                              <span style={{ color: voteInfo.textColor, fontWeight: 500 }}>
                                {voteInfo.text}
                              </span>
                            </Tag>
                          </div>
                        </div>
                      }
                      description={
                        <Text type="secondary">{formatTimestamp(item.timestamp)}</Text>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
